import {useEffect, useRef, useMemo} from 'react';
import {throttle} from 'utils/throttle';
import {hasSession, removeExpiredSessions} from './utils';
import {useReservation} from 'context/reservation';
import {useTimeoutRef} from '@guestapp/core';
import {useChekinSDK} from 'context/ChekinSDK';
import {
  SessionCloseModal,
  type SessionCloseModalRef,
} from './SessionCloseModal/SessionCloseModal';
import {AUTH_INIT_MODELS} from '@guestapp/sdk';

const INACTIVITY_LIMIT = 120 * 1000;
const EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export const IdleDetector = () => {
  const {modelName} = useChekinSDK();
  const timeoutRef = useTimeoutRef();
  const sessionCloseModalRef = useRef<SessionCloseModalRef>(null);
  const {reservationId} = useReservation();

  const openSessionCloseModal = useRef(() => {
    sessionCloseModalRef.current?.open();
  }).current;

  const reportActivity = useMemo(
    () =>
      throttle(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (modelName !== AUTH_INIT_MODELS.housing) {
          timeoutRef.current = setTimeout(openSessionCloseModal, INACTIVITY_LIMIT);
        }
      }, 1000),
    [openSessionCloseModal, timeoutRef, modelName],
  );

  useEffect(
    function addEventListeners() {
      if (hasSession(reservationId)) {
        EVENTS.forEach(event => {
          window.addEventListener(event, reportActivity, {passive: true});
        });
      }

      return () => {
        EVENTS.forEach(event => {
          window.removeEventListener(event, reportActivity);
        });
      };
    },
    [reservationId, reportActivity],
  );

  useEffect(
    function startTimer() {
      clearTimeout(timeoutRef.current);

      if (hasSession(reservationId) && modelName !== AUTH_INIT_MODELS.housing) {
        timeoutRef.current = setTimeout(openSessionCloseModal, INACTIVITY_LIMIT);
      }
    },
    [modelName, openSessionCloseModal, reservationId, timeoutRef],
  );

  useEffect(() => {
    removeExpiredSessions();
  }, []);

  return (
    reservationId && (
      <SessionCloseModal ref={sessionCloseModalRef} reservationId={reservationId} />
    )
  );
};
